import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTheme } from "next-themes";
import { useAuth } from "@/contexts/AuthContext";
import { F, th as palette, Btn } from "@/lib/bricks";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import FintracProgram from "@/components/compliance/FintracProgram";
import { fintracStats, dialogFor } from "@/components/compliance/fintrac";
import { Page, PageHeader, Stats, StatusTabs, List, Row, Empty, Section, Loading, FieldLabel } from "@/components/calm/Calm";

const TYPE_LABEL = { sale: 'Sale (listing side)', purchase: 'Purchase (buyer side)', lease: 'Lease', referral: 'Referral', any: 'Every deal' };
const KINDS = ['General', 'Agreement', 'Amendment', 'Disclosure', 'Waiver', 'Invoice', 'Other'];

function fmtDate(d) {
  if (!d) return '—';
  const dt = new Date(String(d).length === 10 ? `${d}T12:00:00` : d);
  return Number.isNaN(dt.getTime()) ? String(d) : dt.toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' });
}
function fmtAge(h) {
  if (h == null) return '';
  if (h < 1) return 'just now';
  if (h < 48) return `${Math.round(h)}h waiting`;
  return `${Math.round(h / 24)}d waiting`;
}

export default function CompliancePage() {
  const navigate = useNavigate();
  const { resolvedTheme } = useTheme();
  const { session } = useAuth();
  const dark = resolvedTheme === 'dark';
  const p = palette(dark);
  const BASE = process.env.REACT_APP_BACKEND_URL;

  const [params] = useSearchParams();
  const [tab, setTab] = useState(params.get('tab') === 'fintrac' ? 'FINTRAC program' : 'Review queue');
  const [fin, setFin] = useState(null);
  const [finOpen, setFinOpen] = useState(null);
  const [queue, setQueue] = useState(null);
  const [reqs, setReqs] = useState({ requirements: [], can_edit: false });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [rejecting, setRejecting] = useState(null);
  const [rejectNote, setRejectNote] = useState('');
  const [adding, setAdding] = useState(null);
  const [addForm, setAddForm] = useState({ name: '', doc_kind: 'General', required: true });

  const headers = async () => {
    const { data: { session: live } } = await supabase.auth.getSession();
    const token = live?.access_token || session?.access_token;
    return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
  };
  const errText = async (res, fallback) => {
    const b = await res.json().catch(() => ({}));
    if (typeof b.detail === 'string') return b.detail;
    if (b.detail && typeof b.detail.message === 'string') return b.detail.message;
    return fallback;
  };

  const load = useCallback(() => {
    (async () => {
      const h = await headers();
      const [q, r] = await Promise.all([
        fetch(`${BASE}/api/compliance/queue`, { headers: h }).then(x => x.ok ? x.json() : null).catch(() => null),
        fetch(`${BASE}/api/compliance/requirements`, { headers: h }).then(x => x.ok ? x.json() : null).catch(() => null),
      ]);
      const fe = await fetch(`${BASE}/api/fintrac/enabled`, { headers: h }).then(x => x.ok ? x.json() : null).catch(() => null);
      if (fe?.enabled) {
        const fp = await fetch(`${BASE}/api/fintrac/program`, { headers: h }).then(x => x.ok ? x.json() : null).catch(() => null);
        if (fp) setFin(fp);
      }
      if (q) setQueue(q);
      if (r) setReqs(r);
      setLoading(false);
    })();
  }, []); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const review = async (doc, action, note) => {
    setBusy(doc.document_id);
    try {
      const res = await fetch(`${BASE}/api/transactions/${doc.transaction_id}/documents/${doc.document_id}/${action}`, {
        method: 'POST', headers: await headers(), body: JSON.stringify({ note: note || null }),
      });
      if (!res.ok) { toast.error(await errText(res, `Could not ${action} document`)); return false; }
      toast.success(action === 'approve' ? 'Approved' : 'Sent back to the agent');
      load();
      return true;
    } finally { setBusy(''); }
  };

  const saveFintrac = async (method, path, body) => {
    const res = await fetch(`${BASE}/api/fintrac/${path}`, { method, headers: await headers(), body: JSON.stringify(body) });
    if (!res.ok) { toast.error(await errText(res, 'Could not save')); return false; }
    const out = await res.json().catch(() => ({}));
    if (out.program) setFin(out.program);
    toast.success('Saved to your FINTRAC records');
    return true;
  };

  const saveReq = async (id, patch) => {
    const res = await fetch(`${BASE}/api/compliance/requirements/${id}`, { method: 'PATCH', headers: await headers(), body: JSON.stringify(patch) });
    if (!res.ok) { toast.error(await errText(res, 'Could not save')); return; }
    load();
  };
  const addReq = async () => {
    const res = await fetch(`${BASE}/api/compliance/requirements`, {
      method: 'POST', headers: await headers(), body: JSON.stringify({ ...addForm, deal_type: adding }),
    });
    if (!res.ok) { toast.error(await errText(res, 'Could not add')); return; }
    toast.success('Added');
    setAdding(null);
    setAddForm({ name: '', doc_kind: 'General', required: true });
    load();
  };

  if (loading) return <Loading />;

  const canReview = !!queue?.can_review;
  const pending = queue?.pending_documents || [];
  const deals = queue?.deals || [];
  const counts = queue?.counts || {};
  const inputStyle = { width: '100%', padding: '10px 12px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 14, fontFamily: F.display, color: p.ink, background: 'transparent', outline: 'none', boxSizing: 'border-box', marginTop: 8 };
  const link = { background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, fontFamily: F.display, padding: '0 6px', minHeight: 44, minWidth: 44, color: p.ink2, textDecoration: 'underline', textUnderlineOffset: 3, textDecorationColor: p.hairS };

  const onFin = tab === 'FINTRAC program' && !!fin;
  const finNext = onFin && fin.next_action ? (fin.elements || []).find(e => e.key === fin.next_action.key) : null;

  // One main action per view.
  const mainAction = () => {
    if (onFin) return finNext ? <Btn variant="primary" dark={dark} data-testid="fintrac-next" onClick={() => setFinOpen(dialogFor(finNext))}>{fin.next_action.label}</Btn> : null;
    if (tab === 'Required documents') {
      return reqs?.can_edit ? <Btn dark={dark} data-testid="compliance-add-req" onClick={() => { setAdding('any'); setAddForm({ name: '', doc_kind: 'General', required: true }); }}>Add requirement</Btn> : null;
    }
    if (canReview && pending[0]?.transaction_id) {
      return <Btn dark={dark} data-testid="compliance-review-next" onClick={() => navigate(`/transactions/${pending[0].transaction_id}?tab=Documents`)}>Review next document</Btn>;
    }
    if (deals[0]) {
      return <Btn dark={dark} data-testid="compliance-next-file" onClick={() => navigate(`/transactions/${deals[0].transaction_id}?tab=Documents`)}>Open next incomplete file</Btn>;
    }
    return reqs?.can_edit ? <Btn dark={dark} data-testid="compliance-edit-reqs" onClick={() => setTab('Required documents')}>Edit required documents</Btn> : null;
  };

  return (
    <Page testid="compliance-page" width={960}>
      <PageHeader title="Compliance"
        subtitle={onFin
          ? 'Your anti-money laundering program for purchases and sales: the five parts FINTRAC expects, and the records behind them.'
          : canReview
          ? 'Review what agents file, send back what needs fixing, and see which deal files are incomplete.'
          : 'The documents your office requires on your deals, and anything waiting on review or sent back to you.'}
        action={mainAction()} />

      <Stats items={onFin ? fintracStats(fin) : [
        { label: 'Awaiting review', value: counts.pending_documents || 0 },
        { label: 'Incomplete files', value: counts.incomplete_deals || 0 },
        { label: 'Closed with gaps', value: counts.closed_incomplete || 0, attention: !!counts.closed_incomplete },
      ]} />

      <StatusTabs label="View" options={['Review queue', 'Required documents', ...(fin ? ['FINTRAC program'] : [])].map(t => ({ key: t, label: t }))} value={tab} onChange={setTab} testid="compliance-tabs" />

      {onFin && <FintracProgram data={fin} p={p} dark={dark} open={finOpen} setOpen={setFinOpen} save={saveFintrac} />}

      {tab === 'Review queue' && (
        <>
          <Section title="Waiting on review">
            {pending.length === 0 ? <Empty>Nothing waiting on review.</Empty> : (
              <List testid="compliance-pending">
                {pending.map(d => (
                  <Row key={d.document_id} testid={`pending-${d.document_id}`}
                    title={d.name}
                    meta={[d.property_address || 'Deal', d.uploaded_by_name]}
                    next={[d.requirement_name ? `For ${d.requirement_name}` : 'Not filed under a required document', d.age_hours != null ? { text: fmtAge(d.age_hours), attention: (d.age_hours || 0) > 48 } : null]}
                    aside={canReview ? (<>
                      <button disabled={busy === d.document_id} onClick={() => review(d, 'approve')} style={link}>Approve</button>
                      <button disabled={busy === d.document_id} onClick={() => { setRejecting(d); setRejectNote(''); }} style={link}>Send back</button>
                    </>) : <span style={{ fontSize: 13, color: p.ink3 }}>With your broker</span>} />
                ))}
              </List>
            )}
          </Section>

          <Section title="Deal files that aren't complete">
            {deals.length === 0 ? <Empty>Every live deal file is complete.</Empty> : (
              <List testid="compliance-deals">
                {deals.map(d => {
                  const gaps = d.status === 'completed' && !d.complete;
                  return (
                    <Row key={d.transaction_id} testid={`deal-${d.transaction_id}`}
                      onClick={() => navigate(`/transactions/${d.transaction_id}?tab=Documents`)}
                      title={d.property_address || 'Deal'}
                      meta={[TYPE_LABEL[d.deal_type] || d.deal_type, d.agent_name, d.closing_date ? { text: `Closing ${fmtDate(d.closing_date)}` } : null]}
                      next={(d.missing_names || []).length ? `Still needed: ${d.missing_names.join(', ')}` : (d.pending ? 'Waiting on review' : null)}
                      status={<><span style={{ fontFamily: F.mono, fontSize: 12 }}>{d.satisfied}/{d.required_total}</span>{gaps ? ' · Closed with gaps' : d.pending ? ` · ${d.pending} in review` : ''}</>}
                      attention={gaps} />
                  );
                })}
              </List>
            )}
          </Section>
        </>
      )}

      {tab === 'Required documents' && (
        <>
          <div style={{ fontSize: 13, color: p.ink3, maxWidth: 680, lineHeight: 1.5 }}>
            Your office starts on an Ontario list. Edit it to match your policy manual. Anything required must be approved, or marked not applicable with a reason, before a deal file counts as complete. Retired items stay on deals that already used them.
          </div>
          {['sale', 'purchase', 'lease', 'referral', 'any'].map(dt => {
            const rows = (reqs.requirements || []).filter(r => r.deal_type === dt);
            return (
              <Section key={dt} title={TYPE_LABEL[dt]}
                action={reqs.can_edit ? <button style={link} onClick={() => { setAdding(dt); setAddForm({ name: '', doc_kind: 'General', required: true }); }}>Add</button> : null}>
                {rows.length === 0 ? <div style={{ fontSize: 13, color: p.ink3, padding: '8px 4px 4px' }}>{dt === 'any' ? 'Nothing that applies to every deal yet.' : 'Nothing required yet.'}</div> : (
                  <List>
                    {rows.map(r => (
                      <div key={r.id} style={{ opacity: r.active ? 1 : 0.55 }}>
                        <Row testid={`req-${r.id}`}
                          title={<span style={{ textDecoration: r.active ? 'none' : 'line-through' }}>{r.name}</span>}
                          meta={[r.doc_kind, r.active ? null : 'Retired']}
                          status={r.required ? 'Required' : 'Optional'}
                          aside={reqs.can_edit ? (<>
                            {r.active && <button onClick={() => saveReq(r.id, { required: !r.required })} style={link}>{r.required ? 'Make optional' : 'Make required'}</button>}
                            <button onClick={() => saveReq(r.id, { active: !r.active })} style={link}>{r.active ? 'Retire' : 'Restore'}</button>
                          </>) : null} />
                      </div>
                    ))}
                  </List>
                )}
              </Section>
            );
          })}
        </>
      )}

      <Dialog open={!!rejecting} onOpenChange={o => { if (!o) setRejecting(null); }}>
        <DialogContent style={{ maxWidth: 440, borderRadius: 2, background: p.bg, border: `1px solid ${p.hair}`, padding: 'var(--bx-pad)' }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: F.display, fontSize: 18, fontWeight: 500, color: p.ink }}>Send back to the agent</DialogTitle>
            <DialogDescription style={{ fontFamily: F.display, fontSize: 13, color: p.ink3, marginTop: 4 }}>{rejecting?.name} · {rejecting?.property_address}. Say what needs fixing; it's saved with the document and in the deal's review history.</DialogDescription>
          </DialogHeader>
          <textarea rows={4} value={rejectNote} onChange={e => setRejectNote(e.target.value)} placeholder="e.g. Seller ID expiry date is missing" style={{ ...inputStyle, resize: 'vertical' }} />
          <DialogFooter style={{ marginTop: 24, gap: 10 }}>
            <Btn variant="ghost" dark={dark} onClick={() => setRejecting(null)}>Cancel</Btn>
            <Btn variant="primary" dark={dark} disabled={rejectNote.trim().length < 3 || busy === rejecting?.document_id}
              onClick={async () => { if (await review(rejecting, 'reject', rejectNote.trim())) setRejecting(null); }}>Send back</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!adding} onOpenChange={o => { if (!o) setAdding(null); }}>
        <DialogContent style={{ maxWidth: 440, borderRadius: 2, background: p.bg, border: `1px solid ${p.hair}`, padding: 'var(--bx-pad)' }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: F.display, fontSize: 18, fontWeight: 500, color: p.ink }}>Add a document, {(TYPE_LABEL[adding] || '').toLowerCase()}</DialogTitle>
            <DialogDescription style={{ fontFamily: F.display, fontSize: 13, color: p.ink3, marginTop: 4 }}>It shows on every {adding === 'any' ? 'deal' : `${(TYPE_LABEL[adding] || '').toLowerCase()} deal`} from now on, including open ones.</DialogDescription>
          </DialogHeader>
          <div>
            <FieldLabel>Name</FieldLabel>
            <input value={addForm.name} onChange={e => setAddForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Offer summary document" style={inputStyle} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div>
              <FieldLabel>Type</FieldLabel>
              <select value={addForm.doc_kind} onChange={e => setAddForm(f => ({ ...f, doc_kind: e.target.value }))} style={{ ...inputStyle, background: p.bg }}>
                {KINDS.map(k => <option key={k} value={k}>{k}</option>)}
              </select>
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginTop: 24 }}>
              <input type="checkbox" checked={addForm.required} onChange={e => setAddForm(f => ({ ...f, required: e.target.checked }))} style={{ accentColor: p.ink }} />
              Required for a complete file
            </label>
          </div>
          <DialogFooter style={{ marginTop: 24, gap: 10 }}>
            <Btn variant="ghost" dark={dark} onClick={() => setAdding(null)}>Cancel</Btn>
            <Btn variant="primary" dark={dark} disabled={!addForm.name.trim()} onClick={addReq}>Add</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Page>
  );
}
